import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Users, Sparkles, CreditCard, Inbox, Calendar, BookOpen, TrendingUp, Eye,
  Mail, Phone, AlertCircle, BarChart3, Loader2, Bot, Video, MessageSquare, X,
  GraduationCap, Award, Download,
} from "lucide-react";

export default function Owner() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  const [openChat, setOpenChat] = useState(null);
  const refs = {
    leads: useRef(null),
    bookings: useRef(null),
    tutor: useRef(null),
    payments: useRef(null),
    pages: useRef(null),
  };
  const scrollTo = (key) => refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  useEffect(() => {
    api.get("/admin/stats").then(r => setStats(r.data))
      .catch(e => setErr(e?.response?.status === 403 ? "forbidden" : "error"));
  }, []);

  if (err === "forbidden") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-rose-500 mb-4" strokeWidth={2.5}/>
        <h1 className="text-3xl font-black text-slate-900">Owner only</h1>
        <p className="text-slate-600 mt-2">This page is for The Haddis Academy LLC owner. Log in with the academy email to access stats.</p>
        <Link to="/dashboard" className="inline-block mt-6 px-6 py-3 rounded-full bg-slate-900 text-white font-bold">Back to dashboard</Link>
      </div>
    );
  }
  if (err) return <div className="p-12 text-center text-rose-600">Could not load stats.</div>;
  if (!stats) return <div className="p-12 text-center text-slate-500"><Loader2 className="w-6 h-6 mx-auto animate-spin"/> Loading…</div>;

  const u = stats.users;
  const r = stats.revenue;
  const t = stats.traffic;

  return (
    <div data-testid="owner-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">
          <BarChart3 size={14} className="inline -mt-1 mr-1" strokeWidth={3}/> Owner dashboard
        </div>
        <h1 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tight">Hi {user?.parent_name || "Ms. Haddis"} — your numbers</h1>
        <p className="text-slate-600 mt-3">Real-time stats for The Haddis Academy LLC. Updated as parents sign up, subscribe, and book.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Tile icon={Users}    color="sky"     label="Total signups"    value={u.total}  sub={`+${u.new_this_week} this week`}/>
        <Tile icon={Sparkles} color="amber"   label="Active trials"    value={u.active_trial} sub={u.expiring_soon.length ? `${u.expiring_soon.length} expiring soon` : "—"}/>
        <Tile icon={CreditCard} color="emerald" label="Paid subscribers" value={u.active_paid} sub={`$${r.price_usd}/mo each`} onClick={()=>scrollTo("payments")}/>
        <Tile icon={TrendingUp} color="rose"  label="Total revenue"    value={`$${r.total_usd.toFixed(2)}`} sub={`${r.paid_count} payments`} onClick={()=>scrollTo("payments")}/>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Tile icon={Inbox}    color="sky"     label="Enrollment leads" value={stats.leads.total} onClick={()=>scrollTo("leads")}/>
        <Tile icon={Calendar} color="amber"   label="Live bookings"    value={stats.bookings.total} onClick={()=>scrollTo("bookings")}/>
        <Tile icon={Bot}      color="emerald" label="AI tutor sessions" value={stats.tutor?.total_sessions || 0} sub={`${stats.tutor?.total_messages || 0} messages`} onClick={()=>scrollTo("tutor")}/>
        <Tile icon={Eye}      color="rose"    label="Page views (30d)" value={t.page_views_30d} sub={`${t.unique_visitors_30d} unique`} onClick={()=>scrollTo("pages")}/>
      </div>

      {stats.pd && (
        <div className="bg-violet-50 border border-violet-200 rounded-3xl p-6 mb-10">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-violet-700">
              <GraduationCap size={14} strokeWidth={3}/> Professional Development course
            </div>
            <Link to="/professional-development" className="text-xs font-bold text-violet-700 hover:underline">View public page →</Link>
          </div>

          {/* Top-level enrollment numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <PDStat label="Enrolled" value={stats.pd.total} icon={Users}/>
            <PDStat label="Free signups" value={stats.pd.free_count} icon={Award}/>
            <PDStat label="Paid purchases" value={stats.pd.paid_count} icon={CreditCard}/>
            <PDStat label="PD revenue" value={`$${stats.pd.revenue_total.toFixed(2)}`} icon={TrendingUp}/>
          </div>

          {/* Funnel */}
          {stats.pd.funnel && (
            <>
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mt-5 mb-3">Acquisition funnel</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <PDStat label="Landing page views"  value={stats.pd.funnel.landing_views}  sub={`${stats.pd.funnel.landing_unique} unique`} icon={Eye}/>
                <PDStat label="Course page opens"   value={stats.pd.funnel.course_views}   sub={`${stats.pd.funnel.course_unique} unique`} icon={BookOpen}/>
                <PDStat label="Course downloads"    value={stats.pd.funnel.downloads}      sub={`${stats.pd.funnel.downloads_unique} unique`} icon={Download}/>
                <PDStat label="Conversion"          value={stats.pd.funnel.landing_views ? `${Math.round((stats.pd.total / stats.pd.funnel.landing_views) * 100)}%` : "—"} sub="signups ÷ views" icon={TrendingUp}/>
              </div>
            </>
          )}
        </div>
      )}

      {u.expiring_soon.length > 0 && (
        <Section title="Trials expiring within 2 days" icon={AlertCircle} color="amber">
          <div className="space-y-2">
            {u.expiring_soon.map((p, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-2xl bg-amber-50 border border-amber-200 text-sm">
                <div>
                  <div className="font-bold text-slate-900">{p.student_name} <span className="font-normal text-slate-500">· Grade {p.grade}</span></div>
                  <div className="text-slate-600 text-xs">{p.email}</div>
                </div>
                <div className="text-amber-800 font-black text-sm">{p.days_left} {p.days_left === 1 ? "day" : "days"} left</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div ref={refs.leads}>
          <Section title="Enrollment leads (contact form)" icon={Inbox} color="rose">
          {stats.leads.recent.length === 0 ? <Empty msg="No leads yet"/> : (
            <ul className="divide-y divide-slate-100">
              {stats.leads.recent.map((row, i) => (
                <li key={i} className="py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-slate-900">{row.parent_name} <span className="font-normal text-slate-500">· Grade {row.grade}</span></div>
                    <div className="text-xs text-slate-400">{fmtDate(row.created_at)}</div>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs">
                    <a href={`mailto:${row.email}`} className="inline-flex items-center gap-1 text-sky-700 hover:underline"><Mail size={11} strokeWidth={3}/>{row.email}</a>
                    <a href={`tel:${row.phone}`} className="inline-flex items-center gap-1 text-emerald-700 hover:underline"><Phone size={11} strokeWidth={3}/>{row.phone}</a>
                  </div>
                  {row.note && <div className="text-slate-500 mt-1 text-xs italic">"{row.note}"</div>}
                </li>
              ))}
            </ul>
          )}
          </Section>
        </div>

        <div>
          <Section title="Recent signups" icon={Users} color="sky">
            {u.recent.length === 0 ? <Empty msg="No signups yet"/> : (
              <ul className="divide-y divide-slate-100">
                {u.recent.map((row, i) => (
                  <li key={i} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-bold text-slate-900">{row.student_name || row.parent_name} <span className="font-normal text-slate-500">· Grade {row.grade}</span></div>
                      <div className="text-slate-500 text-xs">{row.email}</div>
                    </div>
                    <div className="text-xs text-slate-400">{fmtDate(row.created_at)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div ref={refs.payments}>
          <Section title="Recent payments" icon={CreditCard} color="emerald">
            {r.recent.length === 0 ? <Empty msg="No payments yet"/> : (
              <ul className="divide-y divide-slate-100">
                {r.recent.map((row, i) => (
                  <li key={i} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-bold text-slate-900">${row.amount.toFixed(2)}</div>
                      <div className="text-slate-500 text-xs">{row.user_email}</div>
                    </div>
                    <div className="text-xs text-emerald-700 font-bold">PAID</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <div ref={refs.pages}>
          <Section title="Top pages (last 30 days)" icon={Eye} color="amber">
            {t.by_page.length === 0 ? <Empty msg="No traffic yet"/> : (
              <ul className="divide-y divide-slate-100">
                {t.by_page.map((row, i) => (
                  <li key={i} className="py-3 flex items-center justify-between text-sm">
                    <div className="font-bold text-slate-900 truncate">{row.page}</div>
                    <div className="text-slate-700 font-bold">{row.hits} hits</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>

      <div ref={refs.bookings}>
        <Section title="Upcoming live teacher bookings" icon={Calendar} color="sky">
          {stats.bookings.recent.length === 0 ? <Empty msg="No bookings yet"/> : (
            <ul className="divide-y divide-slate-100">
              {stats.bookings.recent.map((row, i) => (
                <li key={i} className="py-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-black text-slate-900 text-base">{fmtBookingDate(row)}</div>
                      <div className="text-sm text-slate-700 mt-1">
                        <span className="font-bold">{row.student_name || "Student"}</span>
                        {row.grade && <span className="text-slate-500"> · Grade {row.grade}</span>}
                        {row.subject_key && row.subject_key !== "general" && <span className="text-slate-500"> · {prettySubject(row.subject_key)}</span>}
                      </div>
                      {row.parent_name && <div className="text-xs text-slate-500 mt-0.5">Parent: {row.parent_name}</div>}
                      {row.parent_email && (
                        <a href={`mailto:${row.parent_email}`} className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline mt-1">
                          <Mail size={11} strokeWidth={3}/> {row.parent_email}
                        </a>
                      )}
                      {row.notes && <div className="text-xs italic text-slate-500 mt-1">"{row.notes}"</div>}
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {row.meet_link && (
                        <a href={row.meet_link} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold text-xs">
                          <Video size={11} strokeWidth={3}/> Google Meet
                        </a>
                      )}
                      {row.zoom_link && (
                        <a href={row.zoom_link} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-100 hover:bg-sky-200 text-sky-800 font-bold text-xs">
                          <Video size={11} strokeWidth={3}/> Zoom
                        </a>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <div ref={refs.tutor}>
        <Section title="Recent AI tutoring activity" icon={Bot} color="emerald">
          {(!stats.tutor || stats.tutor.recent.length === 0) ? <Empty msg="No AI tutoring sessions yet"/> : (
            <ul className="divide-y divide-slate-100">
              {stats.tutor.recent.map((row, i) => (
                <li key={i} className="py-4" data-testid={`tutor-msg-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 text-sm">
                        {row.student_name}
                        {row.grade && <span className="text-slate-500 font-normal"> · Grade {row.grade}</span>}
                      </div>
                      {row.parent_email && (
                        <a href={`mailto:${row.parent_email}`} className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline mt-0.5">
                          <Mail size={10} strokeWidth={3}/> {row.parent_email}
                        </a>
                      )}
                      <div className="text-xs text-slate-500 mt-1">Lesson <span className="font-mono text-emerald-700">{row.lesson_id}</span></div>
                      {row.user_msg && (
                        <div className="mt-2 p-3 rounded-2xl bg-sky-50 border border-sky-100 text-sm">
                          <div className="text-[10px] font-black uppercase tracking-widest text-sky-700 mb-1">Student asked</div>
                          <div className="text-slate-800">{row.user_msg}</div>
                        </div>
                      )}
                      {row.reply && (
                        <div className="mt-2 p-3 rounded-2xl bg-emerald-50 border border-emerald-100 text-sm">
                          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-1">AI tutor replied</div>
                          <div className="text-slate-800">{row.reply}</div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2 whitespace-nowrap">
                      <div className="text-xs text-slate-400">{fmtDateTime(row.ts)}</div>
                      <button onClick={()=>setOpenChat({ student: row.student_name, grade: row.grade, lesson_id: row.lesson_id, email: row.parent_email })}
                              data-testid={`view-chat-${i}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold text-xs">
                        <MessageSquare size={11} strokeWidth={3}/> Full chat
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <Section title="Lessons completed" icon={BookOpen} color="amber">
        <div className="text-center py-4">
          <div className="text-5xl font-black text-slate-900">{stats.progress.lessons_completed}</div>
          <div className="text-sm text-slate-500 mt-1">quizzes passed across all your students</div>
        </div>
      </Section>

      {stats.pd && (
        <div ref={refs.pd}>
          <Section title="Recent PD enrollments" icon={GraduationCap} color="violet">
            {stats.pd.recent.length === 0 ? <Empty msg="No teachers enrolled yet"/> : (
              <ul className="divide-y divide-slate-100">
                {stats.pd.recent.map((row, i) => (
                  <li key={i} className="py-3 flex items-center justify-between text-sm flex-wrap gap-2">
                    <div>
                      <div className="font-bold text-slate-900">{row.teacher_name}</div>
                      <a href={`mailto:${row.email}`} className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline mt-0.5">
                        <Mail size={11} strokeWidth={3}/> {row.email}
                      </a>
                      {row.school && <div className="text-xs text-slate-500 mt-0.5">{row.school}{row.role ? ` · ${row.role}` : ""}</div>}
                    </div>
                    <div className="text-right">
                      <div className={`text-xs font-black ${row.free_grant ? "text-emerald-700" : "text-violet-700"}`}>
                        {row.free_grant ? "FREE" : `$${(row.amount_paid || 0).toFixed(2)}`}
                      </div>
                      <div className="text-xs text-slate-400">{fmtDate(row.created_at)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}

      {openChat && <ChatModal info={openChat} all={stats.tutor.recent} onClose={()=>setOpenChat(null)} />}
    </div>
  );
}

function ChatModal({ info, all, onClose }) {
  const messages = all.filter(m =>
    m.student_name === info.student && m.grade === info.grade && m.lesson_id === info.lesson_id
  ).slice().reverse();

  return (
    <div data-testid="chat-modal" className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4 animate-pop">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-emerald-600 mb-1">Full conversation</div>
            <div className="font-black text-2xl text-slate-900">{info.student} · Grade {info.grade}</div>
            <div className="text-sm text-slate-500 mt-1">Lesson <span className="font-mono">{info.lesson_id}</span></div>
            {info.email && <div className="text-xs text-slate-500 mt-1">{info.email}</div>}
          </div>
          <button onClick={onClose} data-testid="close-chat" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
            <X size={16} strokeWidth={2.5}/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((m, i) => (
            <div key={i}>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{fmtDateTime(m.ts)}</div>
              <div className="flex justify-end mb-2">
                <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tr-sm bg-sky-500 text-white text-sm leading-relaxed">{m.user_msg}</div>
              </div>
              <div className="flex justify-start">
                <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tl-sm bg-emerald-50 border border-emerald-100 text-slate-800 text-sm leading-relaxed">{m.reply}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function prettySubject(k) {
  return ({ math: "Math", ela: "English", science: "Science", social: "Social Studies" })[k] || k;
}

function fmtBookingDate(row) {
  if (!row) return "";
  if (row.iso_start) {
    try {
      const d = new Date(row.iso_start);
      return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { /* fall through */ }
  }
  // Legacy booking format (yesterday): day + start fields
  if (row.day && row.start) return `${row.day} ${row.start}${row.end ? "–" + row.end : ""}`;
  if (row.created_at) {
    try {
      return "Booked " + new Date(row.created_at).toLocaleDateString();
    } catch { return ""; }
  }
  return "";
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

function Tile({ icon: Icon, color, label, value, sub, onClick }) {
  const colors = {
    sky:     "bg-sky-100 text-sky-700",
    amber:   "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
    rose:    "bg-rose-100 text-rose-700",
  };
  const clickable = typeof onClick === "function";
  return (
    <button type="button" onClick={onClick} disabled={!clickable}
            data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
            className={`text-left bg-white rounded-3xl p-6 border border-slate-100 transition ${clickable ? "hover:-translate-y-0.5 hover:shadow-lg cursor-pointer" : "cursor-default"}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${colors[color]}`}>
        <Icon size={18} strokeWidth={2.5}/>
      </div>
      <div className="text-xs font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-3xl font-black text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      {clickable && <div className="text-[10px] text-sky-600 font-bold mt-2 uppercase tracking-widest">Click to view →</div>}
    </button>
  );
}

function Section({ title, icon: Icon, color, children }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 mb-6">
      <div className={`flex items-center gap-2 text-xs font-black uppercase tracking-widest mb-4 text-${color}-700`}>
        <Icon size={14} strokeWidth={3}/> {title}
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) { return <div className="text-center text-slate-400 text-sm py-6">{msg}</div>; }
function fmtDate(iso) { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } }

function PDStat({ label, value, icon: Icon, sub }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-violet-100">
      <Icon size={16} className="text-violet-600 mb-2" strokeWidth={2.5}/>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-2xl font-black text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
